import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import _throttle from 'lodash/throttle';
import _last from 'lodash/last';
import moment from 'moment';

import { useQuery, useSubscription } from '@apollo/client';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getAreActivitiesEnabled } from 'lib/selectors/getAreActivitiesEnabled';

import {
  SUBSCRIBE_CURRENT_ALARMS,
  SUBSCRIBE_EXECUTION_INTERVALS,
  SUBSCRIBE_MINIMUM_PART_COUNT_EVENTS,
  SUBSCRIBE_LATEST_HEARTBEAT,
  SUBSCRIBE_ACTIVITIES_STATUS,
  SUBSCRIBE_PROGRAM_NAMES,
  SUBSCRIBE_FIRST_PART,
  SUBSCRIBE_LABOR_TICKETS,
  GET_PART_BUCKETS_BY_WINDOW,
} from 'lib/api/queries';

import {
  actionSetAlarms,
  actionSetScopeStatusIntervals,
  actionSetPartCountEvents,
  actionSetLatestHeartbeat,
  actionSetActivitiesStatus,
  actionSetProgramNameData,
  actionSetFirstPart,
  actionSetLastPartTimestamp,
  actionSetLaborTickets,
  actionSetLaborTicketParts,
  actionSetLatestMasterExecutionIntervals,
  actionSetCurrentWorkOrderOp,
} from 'lib/actions';
import { getStartOfSixDaysPrior } from 'lib/selectors/getStartOfSixDaysPrior';
import { getIsBaseAppDataLoading } from 'lib/selectors/getIsBaseAppDataLoading';
import { getIsInitialDataSet } from 'lib/selectors/getIsInitialDataSet';
import { getCompany } from 'lib/selectors/getCompany';
import { getIsInitialActivitySetPollLoading } from 'lib/selectors/getIsInitialActivitySetPollLoading';
import { getHasOpenActivity } from 'lib/selectors/getHasOpenActivity';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { getIsAPMEnabled } from 'lib/selectors/getIsAPMEnabled';
import { getPartKeys } from 'lib/selectors/getPartKeys';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getLaborPartCountQueryIntervals } from 'lib/selectors/getLaborPartCountQueryIntervals';
import { getLastPartTimestamp } from 'lib/selectors/getLastPartTimestamp';
import { formatIntervalsAsTSRange } from 'lib/utils/date';
import { sumAggregatedPartBucketSums } from 'lib/utils/sumAggregatedPartBucketSums';
import { workOrderOpFromLaborTicket } from 'lib/utils/workOrderOpFromLaborTicket';
import { getIsLaborTicketEnabled } from 'lib/selectors/getIsLaborTicketEnabled';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import { getLaborTicketWhereForMachine } from 'lib/selectors/getLaborTicketWhereForMachine';
import { useMachineOperationalStatus } from 'lib/hooks/useMachineOperationalStatus';
import { useNetworkError } from 'lib/hooks';
import { ERROR_KEYS, SIXTY_SECOND_INTERVAL } from 'lib/constants';
// import useTestingIntervals from './useTestingIntervals';

// Doubles per consecutive restart up to the max, then 1-2x jitter so tablets
// don't all reconnect at once. Each subscription error also restarts the shared
// socket (lib/api/links). An error at least five minutes after a restarted
// subscription's first data resets the delay, so a feed the server keeps
// cutting off after a minute or two still backs off.
const EXECUTION_RESUBSCRIBE_DELAY_MS = 5000;
const EXECUTION_RESUBSCRIBE_MAX_DELAY_MS = 5 * 60 * 1000;
const EXECUTION_HEALTHY_AFTER_MS = 5 * 60 * 1000;

function onData(cb) {
  return ({ subscriptionData: { data } }) => {
    return cb(data);
  };
}

const useRealTimeData = ({ onExecutionRecovered, onExecutionError } = {}) => {
  const dispatch = useDispatch();
  const initialLoading = useSelector(getIsBaseAppDataLoading);
  const isInitialDataSet = useSelector(getIsInitialDataSet);
  const isAPMEnabled = useSelector(getIsAPMEnabled);

  const machine = useSelector(getMachine);
  const laborTicketEnabled = useSelector(getIsLaborTicketEnabled);

  const scopeStart = useSelector(getScopeStart);
  const startOfSixDaysPrior = useSelector(getStartOfSixDaysPrior);
  const areActivitiesEnabled = useSelector(getAreActivitiesEnabled);
  const company = useSelector(getCompany);
  const isInitialActivitySetPollLoading = useSelector(
    getIsInitialActivitySetPollLoading
  );
  const hasOpenActivity = useSelector(getHasOpenActivity);
  const isPaused = useSelector(getIsPaused);
  const partKeys = useSelector(getPartKeys);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const clockIn = currentLaborTicket?.clockIn;
  const laborPartCountQueryIntervals = useSelector(
    getLaborPartCountQueryIntervals
  );
  const latestPartTimestamp = useSelector(getLastPartTimestamp);
  const natsFlowEnabled = useSelector(getPostLaborUpdatesOnNats);
  const laborTicketsWhere = useSelector(getLaborTicketWhereForMachine);
  const previousClockInRef = useRef(clockIn);

  useEffect(() => {
    if (clockIn !== previousClockInRef.current) {
      // Reset to a not-yet-loaded 0 until the subscription responds for the new
      // clock-in window, so the labor form doesn't treat this placeholder as
      // authoritative.
      dispatch(actionSetLaborTicketParts(0, false));
    }
    previousClockInRef.current = clockIn;
  }, [clockIn, dispatch]);

  const throttledDispatch = useCallback(
    _throttle((val) => {
      return dispatch(actionSetLastPartTimestamp(val));
    }, 3000),
    []
  );

  useSubscription(SUBSCRIBE_CURRENT_ALARMS, {
    fetchPolicy: 'no-cache',
    shouldResubscribe: true,
    variables: {
      machineRef: machine.machineRef,
    },
    skip: !machine.id,
    onSubscriptionData: onData(({ alarms }) => {
      dispatch(actionSetAlarms(alarms));
    }),
  });

  // Dispatch these for downtime flyup testing
  // Switches Idle > Active every minute
  // useTestingIntervals();

  const fetchMachineOperationalStatus = useMachineOperationalStatus();

  // An errored subscription stays dead until its options change, so drop it for
  // one render to make Apollo subscribe again.
  const [isExecutionResubscribing, setIsExecutionResubscribing] = useState(
    false
  );
  const isAwaitingExecutionRecoveryRef = useRef(false);
  const executionRestartCountRef = useRef(0);
  const executionRecoveredAtRef = useRef(0);
  const { error: executionIntervalsError } = useSubscription(
    SUBSCRIBE_EXECUTION_INTERVALS,
    {
      fetchPolicy: 'no-cache',
      shouldResubscribe: true,
      variables: {
        machineRef: machine.machineRef,
        startDate: scopeStart,
      },
      skip: !scopeStart || !machine.id || isExecutionResubscribing,
      onSubscriptionData: onData(({ executionIntervals }) => {
        dispatch(actionSetLatestMasterExecutionIntervals(executionIntervals));
        dispatch(actionSetScopeStatusIntervals(executionIntervals));
        fetchMachineOperationalStatus();
        if (isAwaitingExecutionRecoveryRef.current) {
          isAwaitingExecutionRecoveryRef.current = false;
          executionRecoveredAtRef.current = Date.now();
          if (onExecutionRecovered) {
            onExecutionRecovered();
          }
        }
      }),
    }
  );

  // Apollo keeps returning the last error until new data arrives, so restart
  // once per error, not on every render that still reports it. A variables
  // change cancels the timer, but that resubscribes on its own.
  const handledExecutionErrorRef = useRef(null);
  // Read through a ref: re-running the effect would cancel a pending restart.
  const onExecutionErrorRef = useRef(onExecutionError);
  onExecutionErrorRef.current = onExecutionError;
  useEffect(() => {
    if (
      !executionIntervalsError ||
      executionIntervalsError === handledExecutionErrorRef.current
    ) {
      return undefined;
    }
    handledExecutionErrorRef.current = executionIntervalsError;
    isAwaitingExecutionRecoveryRef.current = true;
    if (onExecutionErrorRef.current) {
      onExecutionErrorRef.current();
    }
    // Restart only on errors without GraphQL errors: socket failures, and
    // server rejections that websocketlink turns into plain errors. A result
    // carrying GraphQL errors was already replayed by the error link, and
    // Apollo's onError never unsubscribes that replay, so each restart would
    // leave another live subscription behind.
    if (executionIntervalsError.graphQLErrors?.length) {
      return undefined;
    }
    // A clock set back since the recovery can't tell; count it as healthy.
    const recoveredAt = executionRecoveredAtRef.current;
    const sinceRecovered = Date.now() - recoveredAt;
    if (
      recoveredAt &&
      (sinceRecovered < 0 || sinceRecovered >= EXECUTION_HEALTHY_AFTER_MS)
    ) {
      executionRestartCountRef.current = 0;
    }
    executionRecoveredAtRef.current = 0;
    const delay = Math.min(
      EXECUTION_RESUBSCRIBE_DELAY_MS * 2 ** executionRestartCountRef.current,
      EXECUTION_RESUBSCRIBE_MAX_DELAY_MS
    );
    const timeout = setTimeout(() => {
      executionRestartCountRef.current += 1;
      setIsExecutionResubscribing(true);
    }, delay * (1 + Math.random()));
    return () => {
      clearTimeout(timeout);
    };
  }, [executionIntervalsError]);

  useEffect(() => {
    if (isExecutionResubscribing) {
      setIsExecutionResubscribing(false);
    }
  }, [isExecutionResubscribing]);

  useSubscription(SUBSCRIBE_FIRST_PART, {
    fetchPolicy: 'no-cache',
    shouldResubscribe: true,
    variables: {
      machineRef: machine.machineRef,
      startDate: scopeStart,
    },
    skip: !scopeStart || !machine.id,
    onSubscriptionData: onData(({ firstPart }) => {
      dispatch(actionSetFirstPart(firstPart));
    }),
  });

  useSubscription(SUBSCRIBE_MINIMUM_PART_COUNT_EVENTS, {
    fetchPolicy: 'no-cache',
    shouldResubscribe: true,
    variables: {
      machineRef: machine.machineRef,
      startDate: startOfSixDaysPrior,
    },
    skip: !isInitialDataSet || initialLoading || !machine.machineRef,
    onSubscriptionData: onData(({ partCounts }) => {
      // order 5 most recent cycles by asc
      partCounts.sort((a, b) => {
        return moment(a.eventTime).isAfter(moment(b.eventTime)) ? 1 : -1;
      });
      const lastPart = _last(partCounts);
      const lastPartTimestamp = lastPart && lastPart.eventTime;
      dispatch(actionSetPartCountEvents(partCounts));
      throttledDispatch(lastPartTimestamp);
    }),
  });

  useSubscription(SUBSCRIBE_LATEST_HEARTBEAT, {
    fetchPolicy: 'no-cache',
    shouldResubscribe: true,
    variables: {
      machineRef: machine.machineRef,
    },
    skip: !machine.machineRef,
    onSubscriptionData: onData(({ latestHeartbeat }) => {
      dispatch(actionSetLatestHeartbeat(_get(latestHeartbeat, '0', {})));
    }),
  });

  useSubscription(SUBSCRIBE_ACTIVITIES_STATUS, {
    fetchPolicy: 'no-cache',
    shouldResubscribe: true,
    onSubscriptionData: onData(({ Company }) => {
      const activitiesStatus = _get(Company, ['0', 'activitiesStatus'], null);
      dispatch(actionSetActivitiesStatus(activitiesStatus));
    }),
  });

  useSubscription(SUBSCRIBE_PROGRAM_NAMES, {
    fetchPolicy: 'no-cache',
    shouldResubscribe: true,
    skip:
      !areActivitiesEnabled || // fetched via polling for jsg clients
      isInitialActivitySetPollLoading ||
      // optimization: no need for sub when machineHeader will not show program info
      hasOpenActivity ||
      isPaused ||
      !_get(machine, 'machineRef') ||
      !_get(company, 'id'),
    variables: {
      machineRef: machine.machineRef,
      metricKey: machine.programMetric || 'program',
    },
    onSubscriptionData: onData(({ programNames }) => {
      const programNameData = _get(programNames, ['0'], {});
      dispatch(actionSetProgramNameData(programNameData));
    }),
  });

  useSubscription(SUBSCRIBE_LABOR_TICKETS, {
    fetchPolicy: 'no-cache',
    shouldResubscribe: true,
    skip: !machine.machineRef || !laborTicketEnabled || isAPMEnabled,
    variables: {
      where: laborTicketsWhere,
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const { erpLaborTickets } = data;
      dispatch(actionSetLaborTickets(erpLaborTickets));

      // In async flow, the current ticket is the only source of the current
      // work order operation.
      if (natsFlowEnabled) {
        dispatch(
          actionSetCurrentWorkOrderOp(
            workOrderOpFromLaborTicket(erpLaborTickets?.[0])
          )
        );
      }
    },
  });

  // Scheduled and unscheduled windows are disjoint, so counting parts across the union
  // of both interval lists (in one aggregatedCountsByActivitySet call) preserves the
  // #1163 no-double-count fix.
  const laborPartIntervals = formatIntervalsAsTSRange([
    ...(laborPartCountQueryIntervals?.scheduledIntervals || []),
    ...(laborPartCountQueryIntervals?.unscheduledIntervals || []),
  ]);

  // Refetch-on-part-event instead of a self-pushing subscription: the aggregation
  // resolver does not reliably re-emit for a live subscription, which left the
  // "unsubmitted" count stale. Passing lastPartTimestamp (bumped by
  // SUBSCRIBE_MINIMUM_PART_COUNT_EVENTS on every new part) as an extra variable makes
  // Apollo re-run this no-cache query on each new part — the same mechanism that keeps
  // the "You have made" count live via GET_PART_BUCKETS_PARTITIONED_BY_SCHEDULED.
  const requestLaborTicketParts = useQuery(GET_PART_BUCKETS_BY_WINDOW, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      machineRef: machine.machineRef,
      startDate: clockIn,
      partKeys,
      intervals: laborPartIntervals,
      bucketWidth: '24h',
      lastPartTimestamp: latestPartTimestamp,
    },
    skip:
      !laborTicketEnabled ||
      !partKeys ||
      !clockIn ||
      !laborPartCountQueryIntervals ||
      isAPMEnabled,
    onCompleted: ({ aggregatedCountsByActivitySet }) => {
      // Total parts in the scheduled + unscheduled windows (same basis as Utilization).
      const rawParts = sumAggregatedPartBucketSums(
        aggregatedCountsByActivitySet
      );
      dispatch(actionSetLaborTicketParts(rawParts));
    },
  });

  // Surface fetch failures (instead of silently freezing the count with
  // laborTicketPartsLoaded still true) and self-heal: useNetworkError retries with
  // backoff on error, and the pollInterval re-runs the query even if the
  // lastPartTimestamp trigger stalls (e.g. the part-count-events subscription drops).
  useNetworkError(
    requestLaborTicketParts,
    ERROR_KEYS.LABOR_TICKET_PART_BUCKETS,
    {
      pollInterval: SIXTY_SECOND_INTERVAL,
    }
  );
};

export { useRealTimeData };
